"use client"

import { useState, useEffect } from "react"
import { Search, Star, Filter, RefreshCw, Globe, BarChart4 } from "lucide-react"
import { Input } from "../../../components/ui/input"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { Slider } from "../../../components/ui/slider"
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover"
import { Checkbox } from "../../../components/ui/checkbox"
import { Label } from "../../../components/ui/label"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../../components/ui/select"

export default function SupplierFilterBar({ 
  searchTerm, 
  onSearchChange, 
  filters,
  onFilterChange,
  onRefresh,
  supplierCount,
  totalCount
}) {
  const [countries, setCountries] = useState([])
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [tempFilters, setTempFilters] = useState(filters)
  const [activeFilterCount, setActiveFilterCount] = useState(0)
  
  // Lista de países (normalmente vendría de una API)
  const countryList = ["Colombia", "México", "Estados Unidos", "España", "Brasil", "Argentina", "Chile", "Perú", "Canadá"]
  
  useEffect(() => {
    setCountries(countryList)
  }, [])
  
  // Contar filtros activos
  useEffect(() => {
    let count = 0
    if (filters.favorite) count++
    if (filters.country) count++
    if (filters.minRating > 0) count++
    
    setActiveFilterCount(count)
  }, [filters])
  
  // Actualizar filtros temporales cuando cambian los filtros reales
  useEffect(() => {
    setTempFilters(filters)
  }, [filters])
  
  // Aplicar filtros temporales
  const applyFilters = () => {
    onFilterChange(tempFilters)
    setIsPopoverOpen(false)
  }
  
  // Limpiar filtros
  const clearFilters = () => {
    const resetFilters = {
      favorite: false,
      country: null,
      minRating: 0
    }
    setTempFilters(resetFilters)
    onFilterChange(resetFilters)
  }
  
  // Handle para cambio en slider de calificación
  const handleRatingChange = (value) => {
    setTempFilters({...tempFilters, minRating: value[0]})
  }

  return (
    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center px-4 py-3">
      {/* Búsqueda */}
      <div className="relative flex-1 w-full">
        <Input
          type="text"
          placeholder="Buscar proveedores..."
          className="pl-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {/* Filtros */}
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
          >
            <Filter className="h-4 w-4 mr-2 text-slate-600 dark:text-slate-400" />
            <span className="text-slate-700 dark:text-slate-300">Filtros</span>
            {activeFilterCount > 0 && (
              <Badge className="ml-2 bg-orange-500 text-white">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4">
            Filtrar proveedores
          </h3>

          {/* Filtro de favoritos */}
          <div className="mb-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="filter-favorite"
                checked={tempFilters.favorite}
                onCheckedChange={(checked) =>
                  setTempFilters({...tempFilters, favorite: checked})
                }
                className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <Label
                htmlFor="filter-favorite"
                className="flex gap-1 items-center cursor-pointer text-slate-700 dark:text-slate-300"
              >
                <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                <span>Solo favoritos</span>
              </Label>
            </div>
          </div>

          {/* Filtro de país */}
          <div className="mb-4">
            <Label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
              País
            </Label>
            <Select
              value={tempFilters.country || ""}
              onValueChange={(value) =>
                setTempFilters({...tempFilters, country: value || null})
              }
            >
              <SelectTrigger className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700">
                <SelectValue placeholder="Todos los países" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <SelectItem value="">Todos los países</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country} value={country}>
                    {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro de calificación */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <Label className="text-xs text-slate-500 dark:text-slate-400">
                Calificación mínima
              </Label>
              <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
                {tempFilters.minRating}
              </Badge>
            </div>
            <Slider
              value={[tempFilters.minRating]}
              min={0}
              max={5}
              step={0.5}
              onValueChange={handleRatingChange}
              className="[&>[role=slider]]:bg-orange-500"
            />
          </div>

          {/* Action buttons */}
          <div className="flex justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Limpiar
            </Button>
            <Button
              onClick={applyFilters}
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Aplicar filtros
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Botón de refrescar */}
      <Button
        variant="outline"
        size="icon"
        onClick={onRefresh}
        className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700"
      >
        <RefreshCw className="h-4 w-4 text-slate-600 dark:text-slate-400" />
      </Button>

      {/* Contador de resultados */}
      {supplierCount !== totalCount && (
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Mostrando{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {supplierCount}
          </span>{" "}
          de{" "}
          <span className="font-medium text-slate-700 dark:text-slate-300">
            {totalCount}
          </span>{" "}
          proveedores
        </div>
      )}
    </div>
  );
} 